/**
 * What a browser error may carry out of the page.
 *
 * Labs turns on PostHog exception capture, and an error message can quote whatever the
 * code was holding: a JSON.parse error quotes the text it choked on, a validation error
 * quotes the field. On these pages that text is a teacher's lesson plan or a student's
 * writing. So before an `$exception` event leaves the browser, its messages lose email
 * addresses and long quoted strings and are cut to a fixed length, and its URL loses the
 * query and hash. The error type and stack frames go through untouched: they are what
 * make an error fixable, and they are code, not content.
 *
 * Pure, and typed structurally so that `packages/shared` needs no posthog-js of its own.
 * Every other event passes through unchanged.
 */

export const MAX_MESSAGE_LENGTH = 300;

/** Quoted runs longer than this become `[redacted]`. Short ones are usually identifiers. */
export const MAX_QUOTED_LENGTH = 20;

interface ScrubbableEvent {
  event: string;
  properties: Record<string, unknown>;
}

// Deliberately wide: anything around an @ that is not whitespace. Over-redacting a
// quote or bracket next to an address costs nothing; missing an address would.
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
const LONG_QUOTED = new RegExp(`(["'\`])(?:(?!\\1)[\\s\\S]){${MAX_QUOTED_LENGTH + 1},}\\1`, 'g');

export function scrubMessage(message: string): string {
  let scrubbed = message;
  let previous: string;
  // Removing an inner quote can expose a longer outer quote or join an email's
  // parts. Finish those redactions before returning (or truncating) the message.
  // Each replacement removes an @ or shortens a quoted run, so this converges.
  do {
    previous = scrubbed;
    scrubbed = scrubbed.replace(EMAIL, '[email]').replace(LONG_QUOTED, '$1[redacted]$1');
  } while (scrubbed !== previous);
  return scrubbed.length > MAX_MESSAGE_LENGTH ? `${scrubbed.slice(0, MAX_MESSAGE_LENGTH)}…` : scrubbed;
}

export function stripQueryAndHash(url: string): string {
  return url.split(/[?#]/)[0]!;
}

function scrubException(entry: unknown): unknown {
  if (!entry || typeof entry !== 'object') return entry;
  const { value } = entry as { value?: unknown };
  return typeof value === 'string' ? { ...entry, value: scrubMessage(value) } : entry;
}

/** A PostHog `before_send` hook. */
export function scrubExceptionEvent<T extends ScrubbableEvent | null>(event: T): T {
  if (!event || event.event !== '$exception') return event;
  const { $exception_list: list, $current_url: url } = event.properties;
  return {
    ...event,
    properties: {
      ...event.properties,
      ...(Array.isArray(list) ? { $exception_list: list.map(scrubException) } : {}),
      ...(typeof url === 'string' ? { $current_url: stripQueryAndHash(url) } : {}),
    },
  };
}

/**
 * The exception capture every Labs page uses. Set explicitly, so it no longer follows the
 * project-wide toggle in PostHog, which the study app shares. Console errors stay off:
 * libraries log there freely, and a log line is not an error the page failed on.
 */
export const LABS_EXCEPTION_CAPTURE = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
} as const;
